import { z } from 'zod';
import { newId } from '../../domain/id.ts';
import {
  ScheduleColourSchema,
  type ScheduleItem,
  type SchedulePhase,
} from '../../domain/entities/schedule.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { expectUnchanged, updateWrite, type BatchWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';

export interface SchedulePhaseContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

export const SaveSchedulePhaseInputSchema = z.object({
  name: z.string().trim().min(1, 'Give the phase a name').max(120),
  colour: ScheduleColourSchema.optional(),
});
export type SaveSchedulePhaseInput = z.input<typeof SaveSchedulePhaseInputSchema>;
export type UpdateSchedulePhaseInput = Partial<SaveSchedulePhaseInput>;

export class SchedulePhaseValidationError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(fieldErrors: Record<string, string>) {
    const summary = Object.entries(fieldErrors)
      .map(([field, message]) => `${field}: ${message}`)
      .join('; ');
    super(`That phase needs attention — ${summary}`);
    this.name = 'SchedulePhaseValidationError';
    this.fieldErrors = fieldErrors;
  }
}

function parseOrThrow<T extends z.ZodTypeAny>(schema: T, input: unknown): z.output<T> {
  const parsed = schema.safeParse(input);
  if (parsed.success) return parsed.data;

  const fieldErrors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    fieldErrors[issue.path.join('.') || 'form'] ??= issue.message;
  }
  throw new SchedulePhaseValidationError(fieldErrors);
}

function phaseRepository(context: SchedulePhaseContext) {
  return context.data.collection<SchedulePhase>(
    { companyId: context.companyId, jobId: context.jobId },
    'schedulePhases',
  );
}

function itemRepository(context: SchedulePhaseContext) {
  return context.data.collection<ScheduleItem>(
    { companyId: context.companyId, jobId: context.jobId },
    'scheduleItems',
  );
}

export async function listSchedulePhases(context: SchedulePhaseContext): Promise<SchedulePhase[]> {
  const page = await phaseRepository(context).list({
    orderBy: [{ field: 'order', direction: 'asc' }],
  });
  return [...page.items];
}

/** Rejects a name already in use, case-insensitively, ignoring one phase. */
async function assertNameFree(
  context: SchedulePhaseContext,
  name: string,
  exceptId?: string,
): Promise<void> {
  const existing = await listSchedulePhases(context);
  const clash = existing.some(
    (phase) => phase.id !== exceptId && phase.name.toLowerCase() === name.toLowerCase(),
  );
  if (clash) {
    // Two phases with one name make a grouped list ambiguous to read and
    // impossible to reason about in a report.
    throw new SchedulePhaseValidationError({ name: `There is already a phase called ${name}` });
  }
}

export async function createSchedulePhase(
  context: SchedulePhaseContext,
  input: SaveSchedulePhaseInput,
): Promise<SchedulePhase> {
  const parsed = parseOrThrow(SaveSchedulePhaseInputSchema, input);
  await assertNameFree(context, parsed.name);

  const existing = await listSchedulePhases(context);
  // After the highest order rather than at the count: deleting a phase
  // would otherwise hand the next one an order already in use.
  const highest = existing.reduce((max, phase) => Math.max(max, phase.order), -1);
  const now = context.clock.now().toISOString();

  const phase: SchedulePhase = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    name: parsed.name,
    colour: parsed.colour ?? 'slate',
    order: highest + 1,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  await phaseRepository(context).create(phase);
  return phase;
}

export async function updateSchedulePhase(
  context: SchedulePhaseContext,
  id: string,
  input: UpdateSchedulePhaseInput,
): Promise<SchedulePhase> {
  const parsed = parseOrThrow(SaveSchedulePhaseInputSchema.partial(), input);
  const repo = phaseRepository(context);
  const existing = await repo.get(id);
  if (!existing) throw new NotFoundError('schedulePhases', id);

  if (parsed.name !== undefined) await assertNameFree(context, parsed.name, id);

  const next: SchedulePhase = {
    ...existing,
    name: parsed.name ?? existing.name,
    colour: parsed.colour ?? existing.colour,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const { id: _id, ...patch } = next;
  await repo.update(id, patch);
  return next;
}

/**
 * Put the phases in the given order.
 *
 * The whole sequence is required rather than a single move, because a partial
 * reorder has to guess where everything else goes and two people dragging at
 * once would interleave into an order neither chose.
 */
export async function reorderSchedulePhases(
  context: SchedulePhaseContext,
  orderedIds: readonly string[],
): Promise<SchedulePhase[]> {
  const existing = await listSchedulePhases(context);
  const known = new Set(existing.map((phase) => phase.id));
  const given = new Set(orderedIds);

  const coversAll =
    given.size === orderedIds.length &&
    given.size === known.size &&
    orderedIds.every((id) => known.has(id));

  if (!coversAll) {
    throw new SchedulePhaseValidationError({
      form: 'A reorder must list every phase on this job exactly once',
    });
  }

  const now = context.clock.now().toISOString();
  const scope = { companyId: context.companyId, jobId: context.jobId };
  const reordered: SchedulePhase[] = [];
  const writes: BatchWrite[] = [];

  for (const [order, id] of orderedIds.entries()) {
    const phase = existing.find((candidate) => candidate.id === id)!;
    if (phase.order !== order) {
      writes.push(
        updateWrite<SchedulePhase>(
          scope,
          'schedulePhases',
          id,
          { order, updatedAt: now, updatedBy: context.uid },
          expectUnchanged(phase),
        ),
      );
    }
    reordered.push({ ...phase, order });
  }

  // One batch, like every other schedule change. Written one at a time, a
  // failure halfway through leaves two phases claiming the same position and
  // a third claiming none — an order nobody chose, and not one anybody could
  // work out how to undo.
  if (writes.length > 0) await context.data.commit(writes);

  return reordered;
}

/**
 * Delete a phase, ungrouping its items.
 *
 * Items are not deleted with it: a phase is a grouping, and losing a week of
 * planned work because someone tidied up the phase list would be indefensible.
 * Their `phaseId` is cleared, because an item pointing at a phase that no
 * longer exists disappears from any view that renders known phases.
 */
export async function deleteSchedulePhase(
  context: SchedulePhaseContext,
  id: string,
): Promise<void> {
  const repo = phaseRepository(context);
  if (!(await repo.get(id))) throw new NotFoundError('schedulePhases', id);

  const items = itemRepository(context);
  const affected = await items.list({ where: [{ field: 'phaseId', op: '==', value: id }] });
  const now = context.clock.now().toISOString();

  for (const item of affected.items) {
    await items.update(item.id, { phaseId: undefined, updatedAt: now, updatedBy: context.uid });
  }

  await repo.delete(id);
}
